import {
  createIntegrationConnectionSchema,
  exportEntitySchema,
  externalResourceLinksResponseSchema,
  integrationExportResponseSchema,
  updateIntegrationConnectionSchema,
  connectorCatalogResponseSchema,
  connectorCatalogQuerySchema,
  type ConnectorManifest,
  connectorRequestParamsSchema,
  connectorRequestResponseSchema,
  type ExternalResourceLink,
  type IntegrationDeliveryStatus,
  type IntegrationProvider,
  connectionSyncStatusResponseSchema,
  triggerConnectionSyncResponseSchema,
  updateConnectionSyncScheduleSchema,
  updateConnectionSyncScheduleResponseSchema,
  jiraOAuthStartSchema,
  jiraOAuthStartResponseSchema,
  zendeskOAuthStartSchema,
  zendeskOAuthStartResponseSchema,
  hubspotOAuthStartSchema, hubspotOAuthStartResponseSchema,
  intercomOAuthStartSchema, intercomOAuthStartResponseSchema,
  salesforceOAuthStartSchema, salesforceOAuthStartResponseSchema,
  slackOAuthStartSchema, slackOAuthStartResponseSchema,
} from "@io-spoke/contracts/integrations";
import { getApiClient } from "./apiClient";

export type IntegrationConnection = {
  id: string;
  name: string;
  provider: IntegrationProvider;
  status: string;
  isDefault: boolean;
  configSummary: {
    organization?: string;
    project?: string;
    workItemType?: string;
    tenantId?: string;
    clientId?: string;
    siteUrl?: string;
    cloudId?: string;
    authType?: string;
    projectKey?: string;
    issueType?: string;
    jql?: string;
    workflowStateId?: number;
    storyType?: string;
    searchQuery?: string;
    boardId?: string;
    groupId?: string;
    teamId?: string;
    teamName?: string;
    channelId?: string;
    channelName?: string;
    interactive?: boolean;
    createStateId?: string;
    owner?: string;
    repository?: string;
    labels?: string[];
    subdomain?: string;
    tags?: string[];
    accountId?: string;
    workspaceId?: string;
    workspaceName?: string;
    region?: string;
    instanceUrl?: string;
    orgId?: string;
    environment?: string;
  };
  mappings?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
};

export async function listConnectorCatalog(): Promise<ConnectorManifest[]> {
  const response = await getApiClient().get("/integrations/catalog", {
    params: connectorCatalogQuerySchema.parse({ version: "2" }),
  });
  const parsed = connectorCatalogResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid connector catalog.");
  return parsed.data.connectors;
}
export async function requestConnector(providerId: string) {
  const params = connectorRequestParamsSchema.safeParse({ providerId });
  if (!params.success) throw new Error("Unknown integration provider.");
  const response = await getApiClient().post(`/integrations/catalog/${params.data.providerId}/request`);
  const parsed = connectorRequestResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid integration request response.");
  return parsed.data;
}

export async function listIntegrationConnections() {
  return (await getApiClient().get<{ connections: IntegrationConnection[] }>("/integrations/connections")).data.connections;
}
export async function createIntegrationConnection(payload: unknown) {
  const parsed = createIntegrationConnectionSchema.safeParse(payload);
  if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid connection.");
  return (await getApiClient().post("/integrations/connections", parsed.data)).data;
}
export async function updateIntegrationConnection(id: string, payload: unknown) {
  const parsed = updateIntegrationConnectionSchema.safeParse(payload);
  if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid update.");
  return (await getApiClient().patch(`/integrations/connections/${id}`, parsed.data)).data;
}
export async function testIntegrationConnection(id: string) {
  await getApiClient().post(`/integrations/connections/${id}/test`);
}
export async function deleteIntegrationConnection(id: string) {
  await getApiClient().delete(`/integrations/connections/${id}`);
}

export async function startJiraOAuth(payload: unknown) {
  const request = jiraOAuthStartSchema.safeParse(payload);
  if (!request.success) throw new Error(request.error.issues[0]?.message ?? "Invalid Jira settings.");
  const response = await getApiClient().post("/integrations/jira/oauth/start", request.data);
  const parsed = jiraOAuthStartResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid Jira authorization response.");
  return parsed.data;
}

export async function startZendeskOAuth(payload: unknown) {
  const request = zendeskOAuthStartSchema.safeParse(payload);
  if (!request.success) throw new Error(request.error.issues[0]?.message ?? "Invalid Zendesk settings.");
  const response = await getApiClient().post("/integrations/zendesk/oauth/start", request.data);
  const parsed = zendeskOAuthStartResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid Zendesk authorization response.");
  return parsed.data;
}
export async function startHubSpotOAuth(payload: unknown) { const request = hubspotOAuthStartSchema.safeParse(payload); if (!request.success) throw new Error(request.error.issues[0]?.message ?? "Invalid HubSpot settings."); const response = await getApiClient().post("/integrations/hubspot/oauth/start", request.data); const parsed = hubspotOAuthStartResponseSchema.safeParse(response.data); if (!parsed.success) throw new Error("IO Spoke received an invalid HubSpot authorization response."); return parsed.data; }
export async function startIntercomOAuth(payload: unknown) { const request = intercomOAuthStartSchema.safeParse(payload); if (!request.success) throw new Error(request.error.issues[0]?.message ?? "Invalid Intercom settings."); const response = await getApiClient().post("/integrations/intercom/oauth/start", request.data); const parsed = intercomOAuthStartResponseSchema.safeParse(response.data); if (!parsed.success) throw new Error("IO Spoke received an invalid Intercom authorization response."); return parsed.data; }
export async function startSalesforceOAuth(payload: unknown) { const request = salesforceOAuthStartSchema.safeParse(payload); if (!request.success) throw new Error(request.error.issues[0]?.message ?? "Invalid Salesforce settings."); const response = await getApiClient().post("/integrations/salesforce/oauth/start", request.data); const parsed = salesforceOAuthStartResponseSchema.safeParse(response.data); if (!parsed.success) throw new Error("IO Spoke received an invalid Salesforce authorization response."); return parsed.data; }
export async function startSlackOAuth(payload: unknown) { const request = slackOAuthStartSchema.safeParse(payload); if (!request.success) throw new Error(request.error.issues[0]?.message ?? "Invalid Slack settings."); const response = await getApiClient().post("/integrations/slack/oauth/start", request.data); const parsed = slackOAuthStartResponseSchema.safeParse(response.data); if (!parsed.success) throw new Error("IO Spoke received an invalid Slack authorization response."); return parsed.data; }

export async function getConnectionSyncStatus(id: string) {
  const response = await getApiClient().get(`/integrations/connections/${id}/sync`);
  const parsed = connectionSyncStatusResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid integration sync status.");
  return parsed.data;
}

export async function triggerConnectionSync(id: string) {
  const response = await getApiClient().post(`/integrations/connections/${id}/sync`);
  const parsed = triggerConnectionSyncResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid integration sync response.");
  return parsed.data;
}

export async function updateConnectionSyncSchedule(id: string, payload: unknown) {
  const request = updateConnectionSyncScheduleSchema.safeParse(payload);
  if (!request.success) throw new Error(request.error.issues[0]?.message ?? "Invalid synchronization schedule.");
  const response = await getApiClient().patch(`/integrations/connections/${id}/sync/schedule`, request.data);
  const parsed = updateConnectionSyncScheduleResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid synchronization schedule response.");
  return parsed.data;
}

export async function getEntityIntegrationStatus(entityId: string): Promise<{
  links: ExternalResourceLink[];
  deliveries: IntegrationDeliveryStatus[];
}> {
  const response = await getApiClient().get(`/integrations/entities/${entityId}/links`);
  const parsed = externalResourceLinksResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid integration link response.");
  return parsed.data;
}


export async function listEntityIntegrationLinks(entityId: string): Promise<ExternalResourceLink[]> {
  return (await getEntityIntegrationStatus(entityId)).links;
}

export async function retryIntegrationDelivery(deliveryId: string) {
  await getApiClient().post(`/integrations/deliveries/${deliveryId}/retry`);
}

export async function exportEntityToIntegration(connectionId: string, entityId: string) {
  const payload = exportEntitySchema.safeParse({ entityId });
  if (!payload.success) throw new Error(payload.error.issues[0]?.message ?? "Invalid work item export.");
  const response = await getApiClient().post(`/integrations/connections/${connectionId}/export`, payload.data);
  const parsed = integrationExportResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error("IO Spoke received an invalid export response.");
  return parsed.data;
}
